/**
 * The opener of a dossier's source documents, kept out of the component file so that one exports
 * components only. Provided by `SourceDocumentProvider`.
 */

import { createContext, useContext, type MouseEvent } from "react";

export interface SourceDocumentAccess {
  /** Previews a document of the dossier in place, by its id. */
  open: (docId: string) => void;
  /** The address of that document's own page, e.g. for "Ouvrir dans un nouvel onglet". */
  href: (docId: string) => string;
}

/** `null` outside a `SourceDocumentProvider`. */
export const SourceDocumentContext = createContext<SourceDocumentAccess | null>(
  null,
);

export function useSourceDocument(): SourceDocumentAccess | null {
  return useContext(SourceDocumentContext);
}

/** The page of one source document of a dossier. */
export function sourceDocumentPath(
  tenantId: string,
  patientId: string,
  docId: string,
): string {
  return `/patient/${tenantId}/${patientId}/documents/${encodeURIComponent(docId)}`;
}

/** A plain left click — the one a link may take over without stealing the browser's own gestures. */
export function isPlainLeftClick(event: MouseEvent): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

// The badge of a joined file, by MIME type.
const FORMAT_BY_MIME: Record<string, string> = {
  "application/pdf": "PDF",
  "image/jpeg": "JPG",
  "image/png": "PNG",
  "image/tiff": "TIFF",
  "text/html": "HTML",
  "text/plain": "TXT",
  "application/msword": "DOC",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "DOCX",
  "video/mp4": "MP4",
  "video/quicktime": "MOV",
  "video/webm": "WEBM",
  "application/zip": "ZIP",
};

/** A joined file's format — from its MIME type, else its extension; `null` when neither says.
 *  `.bin` says nothing: connectors serve every binary under it. */
export function fileFormatLabel(
  mimeType: string | null,
  fileName: string | null,
): string | null {
  const mime = (mimeType || "").split(";")[0].trim().toLowerCase();
  if (FORMAT_BY_MIME[mime]) return FORMAT_BY_MIME[mime];
  const ext = /\.([a-z0-9]{2,5})$/i.exec(fileName || "")?.[1]?.toLowerCase();
  return ext && ext !== "bin" ? ext.toUpperCase() : null;
}

export function docTypeColor(docType: string | null): string {
  if (!docType) return "bg-slate-100 text-slate-700";
  const colors: Record<string, string> = {
    "Compte-rendu": "bg-blue-100 text-blue-900",
    Imagerie: "bg-primary/10 text-primary",
    Consultation: "bg-slate-100 text-slate-900",
    Biologie: "bg-blue-50 text-blue-700",
  };
  return colors[docType] || "bg-slate-100 text-slate-700";
}
